// ArticleContentWithAds.tsx - Renders article content with comprehensive ad injection
// Ad positions: header, after-foto, cumulative 7-line in-content, end of article

import React, {useCallback, useEffect, useState} from 'react';
import {View, Text, StyleSheet, useWindowDimensions, Linking} from 'react-native';
import WebView from 'react-native-webview';
import RenderHtml from 'react-native-render-html';
import {AdBanner} from '../AdBanner';
import {parseShortcodes, hasShortcodes} from '../../utils/ShortcodeParser';
import {fetchArticleAds, ArticleAds} from '../../services/adService';
import {useTheme} from '../../hooks/useTheme';
import {GalleryImage} from '../../types/article';
import {GalleryCarousel} from './GalleryCarousel';

// Parse poll data from HTML div attributes
const parsePollDiv = (html: string): {pollId: string; question: string; active: boolean; options: {text: string; votes: number}[]} | null => {
  const match = html.match(/data-shortcode="poll"/);
  if (!match) return null;
  
  const pollId = html.match(/data-poll-id="([^"]+)"/)?.[1] || '';
  const question = html.match(/data-question="([^"]+)"/)?.[1] || 'Sondaggio';
  const active = html.match(/data-active="([^"]+)"/)?.[1] === '1';
  const optionsStr = html.match(/data-options="([^"]*)"/)?.[1] || '';
  
  const options = optionsStr.split('|').filter(Boolean).map(opt => {
    const parts = opt.split(':');
    const votes = parseInt(parts.pop() || '0', 10);
    const text = parts.join(':'); // rejoin in case option text has colons
    return {text, votes};
  });
  
  return {pollId, question, active, options};
};

// Parse social embed data from HTML div attributes
const parseSocialEmbed = (html: string): {platform: string; permalink: string} | null => {
  const match = html.match(/data-shortcode="social-embed"/);
  if (!match) return null;
  
  const platform = html.match(/data-platform="([^"]+)"/)?.[1] || '';
  const permalink = html.match(/data-permalink="([^"]+)"/)?.[1] || '';
  if (!permalink) return null;
  
  return {platform, permalink};
};

// Parse diretta-link div from API
const parseDirettaLinkDiv = (html: string): {matchId: string} | null => {
  const match = html.match(/data-shortcode="diretta-link"/);
  if (!match) return null;
  const matchId = html.match(/data-match_id="([^"]+)"/)?.[1];
  if (!matchId) return null;
  return {matchId};
};

// Parse pagelle/pagelle-link div from API
const parsePagelleDiv = (html: string): {matchId: string} | null => {
  const match = html.match(/data-shortcode="pagelle(?:-link)?"/);
  if (!match) return null;
  const matchId = html.match(/data-match_id="([^"]+)"/)?.[1];
  if (!matchId) return null;
  return {matchId};
};

// Parse formazione_risultati div from API
const parseFormazioneRisultatiDiv = (html: string): {matchId: string} | null => {
  const match = html.match(/data-shortcode="formazione_risultati"/);
  if (!match) return null;
  const matchId = html.match(/data-match_id="([^"]+)"/)?.[1];
  if (!matchId) return null;
  return {matchId};
};

// Social embed component using WebView
const SocialEmbed: React.FC<{platform: string; permalink: string; width: number}> = ({platform, permalink, width}) => {
  const [height, setHeight] = useState(450);
  const {isDark} = useTheme();
  
  // Decode HTML entities in the permalink (backend may double-encode &amp;)
  const decodedPermalink = permalink.replace(/&amp;amp;/g, '&').replace(/&amp;/g, '&');
  
  // Normalize x.com URLs to twitter.com for the embed widget
  const normalizedPermalink = decodedPermalink.replace(/^https?:\/\/(www\.)?x\.com\//i, 'https://twitter.com/');

  const onMessage = useCallback((event: any) => {
    try {
      const data = JSON.parse(event.nativeEvent.data);
      if (data.height && data.height > 100) {
        setHeight(data.height);
      }
    } catch {}
  }, []);
  
  // Facebook embed using their SDK
  if (platform === 'facebook' || platform === 'fb') {
    const bgColor = isDark ? '#1a1a2e' : '#ffffff';
    const fbHtml = `
      <!DOCTYPE html>
      <html><head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { background: ${bgColor}; display: flex; justify-content: center; overflow: hidden; }
        .fb-post { width: 100% !important; }
        .fb-post > span { width: 100% !important; }
        .fb-post iframe { width: 100% !important; }
      </style>
      </head><body>
      <div id="fb-root"></div>
      <script async defer crossorigin="anonymous" src="https://connect.facebook.net/it_IT/sdk.js#xfbml=1&version=v21.0" nonce="abc123"></script>
      <div class="fb-post" data-href="${decodedPermalink}" data-width="${width - 48}" data-show-text="true"></div>
      <script>
        function notifyHeight() {
          var h = document.body.scrollHeight;
          if (h > 100) window.ReactNativeWebView.postMessage(JSON.stringify({height: h}));
        }
        window.addEventListener('load', function() { setTimeout(notifyHeight, 2000); setTimeout(notifyHeight, 4000); setTimeout(notifyHeight, 6000); });
        new MutationObserver(notifyHeight).observe(document.body, {childList: true, subtree: true});
      </script>
      </body></html>
    `;
    return (
      <View style={{width: width - 32, height, marginVertical: 12}}>
        <WebView
          source={{html: fbHtml}}
          style={{width: width - 32, height, backgroundColor: 'transparent'}}
          scrollEnabled={false}
          onMessage={onMessage}
          javaScriptEnabled
          domStorageEnabled
          allowsInlineMediaPlayback
          thirdPartyCookiesEnabled
          originWhitelist={['*']}
          onShouldStartLoadWithRequest={(request) => {
            if (request.url !== 'about:blank' && !request.url.startsWith('data:')) {
              if (request.navigationType === 'click') {
                Linking.openURL(request.url);
                return false;
              }
            }
            return true;
          }}
        />
      </View>
    );
  }
  
  const getEmbedHtml = () => {
    const bgColor = isDark ? '#1a1a2e' : '#ffffff';
    const textColor = isDark ? '#e0e0e0' : '#000000';
    
    const baseStyles = `
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { background: ${bgColor}; color: ${textColor}; display: flex; justify-content: center; overflow: hidden; }
      .container { width: 100%; max-width: ${width - 32}px; }
      iframe { max-width: 100% !important; }
      blockquote { max-width: 100% !important; min-width: 0 !important; width: 100% !important; }
    `;
    
    const heightScript = `
      function notifyHeight() {
        var h = document.body.scrollHeight;
        if (h > 100) window.ReactNativeWebView.postMessage(JSON.stringify({height: h}));
      }
      window.addEventListener('load', function() { setTimeout(notifyHeight, 1500); setTimeout(notifyHeight, 3000); setTimeout(notifyHeight, 5000); });
      new MutationObserver(notifyHeight).observe(document.body, {childList: true, subtree: true});
    `;
    
    if (platform === 'twitter' || platform === 'x') {
      const theme = isDark ? 'dark' : 'light';
      return `
        <!DOCTYPE html>
        <html><head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
        <style>${baseStyles}</style>
        </head><body>
        <div class="container">
          <blockquote class="twitter-tweet" data-theme="${theme}" data-width="${width - 48}">
            <a href="${normalizedPermalink}"></a>
          </blockquote>
        </div>
        <script async src="https://platform.twitter.com/widgets.js" charset="utf-8"></script>
        <script>${heightScript}</script>
        </body></html>
      `;
    }
    
    // Instagram
    return `
      <!DOCTYPE html>
      <html><head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
      <style>${baseStyles}</style>
      </head><body>
      <div class="container">
        <blockquote class="instagram-media" data-instgrm-captioned data-instgrm-permalink="${decodedPermalink}" style="width:100%;">
        </blockquote>
      </div>
      <script async src="https://www.instagram.com/embed.js"></script>
      <script>${heightScript}</script>
      </body></html>
    `;
  };
  
  const embedHtml = getEmbedHtml();
  
  return (
    <View style={{width: width - 32, height, marginVertical: 12}}>
      <WebView
        source={{html: embedHtml}}
        style={{width: width - 32, height, backgroundColor: 'transparent'}}
        scrollEnabled={false}
        onMessage={onMessage}
        javaScriptEnabled
        domStorageEnabled
        allowsInlineMediaPlayback
        thirdPartyCookiesEnabled
        originWhitelist={['*']}
        onShouldStartLoadWithRequest={(request) => {
          if (request.url !== 'about:blank' && !request.url.startsWith('data:')) {
            if (request.navigationType === 'click') {
              Linking.openURL(request.url);
              return false;
            }
          }
          return true;
        }}
      />
    </View>
  );
};

// Poll results card component
const PollCard: React.FC<{question: string; active: boolean; options: {text: string; votes: number}[]; colors: any}> = ({question, active, options, colors}) => {
  const totalVotes = options.reduce((sum, o) => sum + o.votes, 0);
  
  return (
    <View style={[pollStyles.card, {backgroundColor: colors.surface, borderColor: colors.border}]}>
      <View style={pollStyles.header}>
        <Text style={[pollStyles.label, {color: colors.primary}]}>SONDAGGIO</Text>
        {!active && (
          <View style={pollStyles.closedBadge}>
            <Text style={pollStyles.closedText}>Chiuso</Text>
          </View>
        )}
      </View>
      <Text style={[pollStyles.question, {color: colors.textPrimary}]}>{question}</Text>
      {options.map((opt, i) => {
        const pct = totalVotes > 0 ? Math.round((opt.votes / totalVotes) * 100) : 0;
        return (
          <View key={i} style={pollStyles.optionRow}>
            <View style={pollStyles.optionHeader}>
              <Text style={[pollStyles.optionText, {color: colors.textPrimary}]} numberOfLines={1}>{opt.text}</Text>
              <Text style={[pollStyles.pctText, {color: colors.textSecondary}]}>{pct}%</Text>
            </View>
            <View style={[pollStyles.barBg, {backgroundColor: colors.border}]}>
              <View style={[pollStyles.barFill, {width: `${pct}%`, backgroundColor: colors.primary}]} />
            </View>
          </View>
        );
      })}
      <Text style={[pollStyles.totalVotes, {color: colors.textSecondary}]}>
        {totalVotes} vot{totalVotes === 1 ? 'o' : 'i'} totali
      </Text>
    </View>
  );
};

const pollStyles = StyleSheet.create({
  card: {
    borderRadius: 12,
    borderWidth: 1,
    padding: 16,
    marginVertical: 12,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  label: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 1,
  },
  closedBadge: {
    backgroundColor: '#ef4444',
    borderRadius: 4,
    paddingHorizontal: 8,
    paddingVertical: 2,
  },
  closedText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: '700',
  },
  question: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 14,
    lineHeight: 22,
  },
  optionRow: {
    marginBottom: 10,
  },
  optionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  optionText: {
    fontSize: 14,
    flex: 1,
  },
  pctText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 8,
  },
  barBg: {
    height: 6,
    borderRadius: 3,
    overflow: 'hidden',
  },
  barFill: {
    height: 6,
    borderRadius: 3,
  },
  totalVotes: {
    fontSize: 12,
    marginTop: 6,
    textAlign: 'right',
  },
});

interface ArticleContentWithAdsProps {
  content: string;
  galleries?: GalleryImage[];
  showCover?: boolean;
}

// ~70 characters per line, 7 lines = 490 characters
const CHARS_PER_LINE = 70;
const LINES_THRESHOLD = 7;

/**
 * Split HTML content into paragraphs for ad injection
 */
const splitIntoParagraphs = (html: string): string[] => {
  const paragraphRegex = /<p[^>]*>[\s\S]*?<\/p>|<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>|<shortcode>[\s\S]*?<\/shortcode>|<figure[^>]*>[\s\S]*?<\/figure>|<div[^>]*>[\s\S]*?<\/div>|<ul[^>]*>[\s\S]*?<\/ul>|<ol[^>]*>[\s\S]*?<\/ol>|<blockquote[^>]*>[\s\S]*?<\/blockquote>|<table[^>]*>[\s\S]*?<\/table>/gi;
  const matches = html.match(paragraphRegex);
  return matches || [];
};

/**
 * Get text length from HTML paragraph (for line counting)
 */
const getParagraphTextLength = (html: string): number => {
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length;
};

/**
 * Estimate lines from character count
 */
const estimateLines = (charCount: number): number => {
  return Math.ceil(charCount / CHARS_PER_LINE);
};

/**
 * Pre-process content to convert generic HTML pagelle CTAs to shortcodes for native handling
 */
const processPagelleLinks = (html: string) => {
  // Regex to find the pagelle/formazione link pattern in HTML and convert to shortcode
  // Pattern matches the div wrapper and optional match_id from the URL
  // Handles multi-class attributes and missing match_id (defaults to latest)
  return html.replace(
    /<div[^>]*class=["'][^"']*lv-cta-pagelle[^"']*["'][^>]*>([\s\S]*?)<\/div>/gi, 
    (fullMatch, content) => {
      const idMatch = content.match(/match_id=(\d+)/);
      const id = idMatch ? idMatch[1] : 'latest';
      return `[pagelle-link match="${id}"]`;
    }
  );
};

export const ArticleContentWithAds: React.FC<ArticleContentWithAdsProps> = ({
  content,
  galleries,
  showCover = true,
}) => {
  const {width} = useWindowDimensions();
  const {colors} = useTheme();
  
  // Process content to ensure native handling of Pagelle buttons
  const processedContent = React.useMemo(() => processPagelleLinks(content), [content]);
  
  const [ads, setAds] = useState<ArticleAds>({
    header: null,
    afterFoto: null,
    inContent: [],
    endArticle: null,
    afterForm: null,
    afterThirdComment: null,
    afterAllComments: null,
  });
  const [adsLoaded, setAdsLoaded] = useState(false);

  // Fetch ads on mount
  useEffect(() => {
    const loadAds = async () => {
      const fetchedAds = await fetchArticleAds(10); // Request up to 10 in-content ads
      setAds(fetchedAds);
      setAdsLoaded(true);
    };
    loadAds();
  }, []);

  const tagsStyles = {
    body: {
      color: colors.textPrimary,
      fontSize: 16,
      lineHeight: 26,
    },
    p: {
      marginBottom: 16,
    },
    h2: {
      color: colors.primary,
      fontWeight: '700' as const,
      marginTop: 24,
      marginBottom: 12,
    },
    h3: {
      color: colors.primary,
      fontWeight: '600' as const,
      marginTop: 20,
      marginBottom: 10,
    },
    a: {
      color: colors.primary,
      textDecorationLine: 'underline' as const,
    },
    blockquote: {
      borderLeftWidth: 4,
      borderLeftColor: colors.primary,
      paddingLeft: 16,
      marginVertical: 16,
      fontStyle: 'italic' as const,
      color: colors.textSecondary,
    },
    strong: {
      fontWeight: '700' as const,
    },
    b: {
      fontWeight: '700' as const,
    },
    em: {
      fontStyle: 'italic' as const,
    },
    i: {
      fontStyle: 'italic' as const,
    },
    figure: {
      marginVertical: 16,
      alignItems: 'center' as const,
    },
    img: {
      maxWidth: width - 32,
    },
  };

  const classesStyles = {
    'lv-cta-pagelle': {
      backgroundColor: colors.primary,
      borderRadius: 12,
      paddingVertical: 16,
      paddingHorizontal: 24,
      marginVertical: 20,
      alignItems: 'center' as const,
    },
    'lv-cta-pagelle-link': {
      color: '#FFFFFF',
      fontSize: 16,
      fontWeight: '700' as const,
      textAlign: 'center' as const,
      textDecorationLine: 'none' as const,
    },
  };

  // Detect if content has a CTA button (pagelle) - skip in-content ads to keep CTA prominent
  // Check for both legacy class and new shortcode
  const hasCTA = content.includes('lv-cta-pagelle') || processedContent.includes('[pagelle');

  // Build content with ads injected
  const buildContentWithAds = () => {
    const elements: React.ReactNode[] = [];
    
    // Note: Header ad is now placed in ArticleScreen, before category/title

    // 1. After foto ad (only when cover image is shown)
    if (showCover !== false) {
      elements.push(
        <AdBanner
          key="after-foto-ad"
          position="middle"
          imageUrl={ads.afterFoto?.image_url || undefined}
          targetUrl={ads.afterFoto?.target_url || undefined}
          showPlaceholder={!adsLoaded}
        />
      );
    }

    // Check if content has shortcodes
    if (hasShortcodes(processedContent)) {
      // Parse the content into segments and inject ads between them
      const parsed = parseShortcodes(processedContent);
      let adIndex = 0;
      let accumulatedChars = 0;

      parsed.segments.forEach((segment, segIndex) => {
        if (segment.type === 'html') {
          // Split this HTML segment into paragraphs for proper ad injection
          const paragraphs = splitIntoParagraphs(segment.content);
          
          if (paragraphs.length === 0) {
            // No paragraphs found, render as-is
            elements.push(
              <RenderHtml
                key={`html-${segIndex}`}
                contentWidth={width - 32}
                baseStyle={{maxWidth: width - 32}}
                source={{html: segment.content}}
                tagsStyles={tagsStyles}
                classesStyles={classesStyles}
                enableExperimentalMarginCollapsing
                ignoredDomTags={['shortcode', 'script', 'style']}
              />
            );
            accumulatedChars += getParagraphTextLength(segment.content);
          } else {
            // Process each paragraph with ad injection
            paragraphs.forEach((paragraph, pIndex) => {
              const paragraphLength = getParagraphTextLength(paragraph);
              accumulatedChars += paragraphLength;

              // Check for poll div
              const pollData = parsePollDiv(paragraph);
              const socialData = parseSocialEmbed(paragraph);
              const direttaData = parseDirettaLinkDiv(paragraph);
              const pagelleData = parsePagelleDiv(paragraph);
              const formazioneData = parseFormazioneRisultatiDiv(paragraph);
              if (pollData) {
                elements.push(
                  <PollCard
                    key={`poll-${segIndex}-${pIndex}`}
                    question={pollData.question}
                    active={pollData.active}
                    options={pollData.options}
                    colors={colors}
                  />
                );
              } else if (socialData) {
                elements.push(
                  <SocialEmbed
                    key={`social-${segIndex}-${pIndex}`}
                    platform={socialData.platform}
                    permalink={socialData.permalink}
                    width={width}
                  />
                );
              } else if (direttaData || pagelleData || formazioneData) {
                const {ShortcodeSegment: SC} = require('./ShortcodeSegment');
                const shortcodeType = direttaData ? 'diretta-link' : pagelleData ? 'pagelle' : 'formazione-risultati';
                const scMatchId = direttaData?.matchId || pagelleData?.matchId || formazioneData?.matchId;
                elements.push(
                  <SC
                    key={`sc-div-${segIndex}-${pIndex}`}
                    shortcode={{type: shortcodeType, matchId: scMatchId, originalMatch: paragraph}}
                  />
                );
              } else {
                // Add the paragraph
                elements.push(
                  <RenderHtml
                    key={`html-${segIndex}-p-${pIndex}`}
                    contentWidth={width - 32}
                    baseStyle={{maxWidth: width - 32}}
                    source={{html: paragraph}}
                    tagsStyles={tagsStyles}
                    classesStyles={classesStyles}
                    enableExperimentalMarginCollapsing
                    ignoredDomTags={['shortcode', 'script', 'style']}
                  />
                );
              }

              // Check if we should inject an ad after this paragraph
              const accumulatedLines = estimateLines(accumulatedChars);
              const hasMoreAds = adIndex < ads.inContent.length;
              const notLastParagraph = pIndex < paragraphs.length - 1 || segIndex < parsed.segments.length - 1;

              if (accumulatedLines >= LINES_THRESHOLD && hasMoreAds && notLastParagraph && !hasCTA) {
                const ad = ads.inContent[adIndex];
                elements.push(
                  <AdBanner
                    key={`in-content-ad-${adIndex}`}
                    position="middle"
                    imageUrl={ad.image_url || undefined}
                    targetUrl={ad.target_url || undefined}
                  />
                );
                adIndex++;
                accumulatedChars = 0;
              }
            });
          }
        } else if (segment.type === 'shortcode' && segment.shortcode) {
          // Render shortcode
          const {ShortcodeSegment} = require('./ShortcodeSegment');
          elements.push(
            <ShortcodeSegment
              key={`sc-${segIndex}`}
              shortcode={segment.shortcode}
              galleries={galleries}
            />
          );
          // Shortcodes don't add to line count, but check if we need an ad after
        }
      });
    } else {
      // Split content into paragraphs and inject ads based on cumulative line count
      const paragraphs = splitIntoParagraphs(processedContent);
      let adIndex = 0;
      let accumulatedChars = 0; // Track cumulative characters since last ad

      paragraphs.forEach((paragraph, index) => {
        const paragraphLength = getParagraphTextLength(paragraph);
        accumulatedChars += paragraphLength;

        // Check for poll div or social embed or shortcode divs
        const pollData = parsePollDiv(paragraph);
        const socialData = parseSocialEmbed(paragraph);
        const direttaData = parseDirettaLinkDiv(paragraph);
        const pagelleData = parsePagelleDiv(paragraph);
        const formazioneData = parseFormazioneRisultatiDiv(paragraph);
        if (pollData) {
          elements.push(
            <PollCard
              key={`poll-${index}`}
              question={pollData.question}
              active={pollData.active}
              options={pollData.options}
              colors={colors}
            />
          );
        } else if (socialData) {
          elements.push(
            <SocialEmbed
              key={`social-${index}`}
              platform={socialData.platform}
              permalink={socialData.permalink}
              width={width}
            />
          );
        } else if (direttaData || pagelleData || formazioneData) {
          const {ShortcodeSegment: SC} = require('./ShortcodeSegment');
          const shortcodeType = direttaData ? 'diretta-link' : pagelleData ? 'pagelle' : 'formazione-risultati';
          const scMatchId = direttaData?.matchId || pagelleData?.matchId || formazioneData?.matchId;
          elements.push(
            <SC
              key={`sc-div-${index}`}
              shortcode={{type: shortcodeType, matchId: scMatchId, originalMatch: paragraph}}
            />
          );
        } else {
          // Add the paragraph
          elements.push(
            <RenderHtml
              key={`p-${index}`}
              contentWidth={width - 32}
              baseStyle={{maxWidth: width - 32}}
              source={{html: paragraph}}
              tagsStyles={tagsStyles}
              classesStyles={classesStyles}
              enableExperimentalMarginCollapsing
              ignoredDomTags={['shortcode', 'script', 'style']}
            />
          );

          // Check if accumulated lines >= 7 (cumulative logic)
          const accumulatedLines = estimateLines(accumulatedChars);
          const hasMoreAds = adIndex < ads.inContent.length;
          const notLastParagraph = index < paragraphs.length - 1;

          if (accumulatedLines >= LINES_THRESHOLD && hasMoreAds && notLastParagraph && !hasCTA) {
            const ad = ads.inContent[adIndex];
            elements.push(
              <AdBanner
                key={`in-content-ad-${adIndex}`}
                position="middle"
                imageUrl={ad.image_url || undefined}
                targetUrl={ad.target_url || undefined}
              />
            );
            adIndex++;
            accumulatedChars = 0; // Reset after placing ad
          }
        }
      });
    }

    // 3. Gallery carousel (before end-of-article ad)
    if (galleries && galleries.length > 0) {
      elements.push(
        <GalleryCarousel key="gallery" images={galleries} />
      );
    }

    // 4. End of article ad
    if (ads.endArticle) {
      elements.push(
        <AdBanner
          key="end-article-ad"
          position="bottom"
          imageUrl={ads.endArticle.image_url || undefined}
          targetUrl={ads.endArticle.target_url || undefined}
        />
      );
    }

    return elements;
  };

  return <View>{buildContentWithAds()}</View>;
};

// Export comment ads for use in Comments component
export const useCommentAds = () => {
  const [ads, setAds] = useState<{
    afterForm: ArticleAds['afterForm'];
    afterThirdComment: ArticleAds['afterThirdComment'];
    afterAllComments: ArticleAds['afterAllComments'];
  }>({
    afterForm: null,
    afterThirdComment: null,
    afterAllComments: null,
  });

  useEffect(() => {
    const loadAds = async () => {
      const fetchedAds = await fetchArticleAds(0); // Just get the comment ads
      setAds({
        afterForm: fetchedAds.afterForm,
        afterThirdComment: fetchedAds.afterThirdComment,
        afterAllComments: fetchedAds.afterAllComments,
      });
    };
    loadAds();
  }, []);

  return ads;
};

export default ArticleContentWithAds;
